import type { BoardRunView } from '@goodtime/engine';
import { friendOf, lampGameOf, type BoardStopResult, type RoadLamp, type RunPot } from '@goodtime/shared';

// Type-only engine import: this file is tested without Pixi.
const potEmpty = (pot: RunPot) => pot.coins === 0 && pot.gems === 0 && pot.freeSpins === 0;

/** What a landing paid, in a few words. `lamps` are the road's own, which
 *  name the friend behind each lamp and the game it offers; on a Wheel Run
 *  slot (`themeId`) the game is named after the slot. */
export function landingText(result: BoardStopResult, lamps: readonly RoadLamp[] = [], themeId: string | null = null): string {
  // A lit lamp: its friend's game opens (and pays the pot), so it's no loss.
  // The lamp landed on names them: another road's friend may bring the same
  // game, and with Gamble off a lamp offers a stand-in under its own name.
  if (result.child) {
    const lamp = lamps.find((l) => l.id === result.kind);
    const friend = lamp ? friendOf(lamp) : null;
    if (friend) return `${friend.name}’s lamp opens ${friend.title}!`;
    const game = lamp && themeId ? lampGameOf(themeId, lamp) : null;
    return game ? `The lamp opens ${game.title}!` : 'A lamp! Its game opens.';
  }
  switch (result.kind) {
    case 'bomb':
      return 'Bomb! You lose a life.';
    case 'silver':
      return `Silver pot! +${result.prize.coins.toLocaleString('en-GB')} coins`;
    case 'free_spins':
      return `+${result.prize.freeSpins} free spins`;
    default:
      return result.prize.coins > 0 ? `+${result.prize.coins.toLocaleString('en-GB')} coins` : 'Nothing there this time.';
  }
}

/** The Lamp Run's status line for each phase, on a road with `lamps` (on
 *  slot `themeId`). Every ending says what happened in a kind line; an empty
 *  pot reads DONE, not COLLECT. */
export function boardStatus(view: BoardRunView, lamps: readonly RoadLamp[] = [], themeId: string | null = null): string {
  switch (view.phase) {
    case 'ready':
      if (view.stops === 0) {
        return view.lucky ? 'No bombs this time. Tap STOP when you like.' : 'Tap STOP to see where the light lands.';
      }
      return 'Tap STOP for the next one.';
    case 'landing':
      return 'Round and round it goes…';
    case 'result':
      return view.last ? landingText(view.last, lamps, themeId) : '';
    case 'over':
      return potEmpty(view.pot) ? 'No prizes this run. The board will be back soon.' : 'Run over. Tap COLLECT for your pot.';
    case 'collecting':
      return 'Collecting your pot…';
    default:
      return '';
  }
}
